import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FaTrash, FaEdit, FaUsers } from "react-icons/fa";

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    name: "",
    date: "",
    location: "",
    description: "",
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(10);

  const navigate = useNavigate();
  const { user, fetchWithRetry, isLoading, logout } = useAuth();

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/admin/events",
      });
      setEvents(res.data);
      setFilteredEvents(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Lỗi tải danh sách sự kiện:", error);
      setError(
        error.response?.data?.message ||
        (error.response?.status === 403
          ? "Bạn không có quyền truy cập trang này! Vui lòng đăng nhập lại."
          : "Không thể tải danh sách sự kiện!")
      );
      if (error.response?.status === 403 || error.response?.status === 401) {
        setTimeout(() => logout(), 3000);
      }
      setLoading(false);
    }
  }, [fetchWithRetry, logout]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/admin/users",
      });
      setUsers(res.data);
    } catch (error) {
      console.error("Lỗi tải danh sách người dùng:", error);
      setError(error.response?.data?.message || "Không thể tải danh sách người dùng!");
    }
  }, [fetchWithRetry]);

  const handleAddEvent = useCallback(async () => {
    if (!newEvent.name || !newEvent.date || !newEvent.location) {
      setError("Vui lòng nhập đầy đủ tên, ngày diễn ra và địa điểm sự kiện!");
      return;
    }

    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/events",
        data: newEvent,
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setNewEvent({ name: "", date: "", location: "", description: "" });
      setShowAddModal(false);
      await loadEvents();
    } catch (error) {
      console.error("Lỗi thêm sự kiện:", error);
      setError(error.response?.data?.message || "Không thể thêm sự kiện!");
      setTimeout(() => setError(null), 3000);
    }
  }, [newEvent, loadEvents, fetchWithRetry]);

  const handleEditEvent = useCallback(async () => {
    if (!editingEvent.name || !editingEvent.date || !editingEvent.location) {
      setError("Vui lòng nhập đầy đủ tên, ngày diễn ra và địa điểm sự kiện!");
      return;
    }

    try {
      const response = await fetchWithRetry({
        method: "put",
        url: `/api/admin/events/${editingEvent._id}`,
        data: {
          name: editingEvent.name,
          date: editingEvent.date,
          location: editingEvent.location,
          description: editingEvent.description,
        },
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditingEvent(null);
      setShowEditModal(false);
      await loadEvents();
    } catch (error) {
      console.error("Lỗi cập nhật sự kiện:", error);
      setError(error.response?.data?.message || "Không thể cập nhật sự kiện!");
      setTimeout(() => setError(null), 3000);
    }
  }, [editingEvent, loadEvents, fetchWithRetry]);

  const handleDeleteEvent = useCallback(
    async (id) => {
      if (!window.confirm("Bạn có chắc muốn xóa sự kiện này?")) return;
      try {
        const response = await fetchWithRetry({
          method: "delete",
          url: `/api/admin/events/${id}`,
        });
        setSuccessMessage(response.data.message);
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadEvents();
      } catch (error) {
        console.error("Lỗi xóa sự kiện:", error);
        setError(error.response?.data?.message || "Không thể xóa sự kiện!");
        setTimeout(() => setError(null), 3000);
      }
    },
    [fetchWithRetry, loadEvents]
  );

  const handleJoinEvent = useCallback(
    async (eventId, userId) => {
      try {
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/admin/events/join",
          data: { eventId, userId },
        });
        setSuccessMessage(response.data.message);
        setTimeout(() => setSuccessMessage(null), 3000);
        const joinedUser = users.find((u) => u._id === userId);
        setParticipants((prev) => [...prev, joinedUser]);
        await loadEvents();
      } catch (error) {
        console.error("Lỗi thêm người tham gia:", error);
        setError(error.response?.data?.message || "Không thể thêm người tham gia!");
        setTimeout(() => setError(null), 3000);
      }
    },
    [fetchWithRetry, users, loadEvents]
  );

  const handleLeaveEvent = useCallback(
    async (eventId, userId) => {
      try {
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/admin/events/leave",
          data: { eventId, userId },
        });
        setSuccessMessage(response.data.message);
        setTimeout(() => setSuccessMessage(null), 3000);
        setParticipants((prev) => prev.filter((p) => p._id !== userId));
        await loadEvents();
      } catch (error) {
        console.error("Lỗi xóa người tham gia:", error);
        setError(error.response?.data?.message || "Không thể xóa người tham gia!");
        setTimeout(() => setError(null), 3000);
      }
    },
    [fetchWithRetry, loadEvents]
  );

  const handleViewParticipants = (event) => {
    setSelectedEventId(event._id);
    setParticipants(event.participants || []);
    setShowParticipantsModal(true);
  };

  useEffect(() => {
    let filtered = [...events];

    if (searchTerm) {
      filtered = filtered.filter((event) =>
        event.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((event) => event.status === filterStatus);
    }

    setFilteredEvents(filtered);
    setCurrentPage(1);
  }, [events, searchTerm, filterStatus]);

  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    if (isLoading) return;

    if (!user || !user.permissions.includes("manage_events")) {
      setError("Bạn không có quyền truy cập trang này!");
      navigate("/login");
      return;
    }
    loadEvents();
    loadUsers();
  }, [user, navigate, loadEvents, loadUsers, isLoading]);

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Quản lý sự kiện</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
          disabled={loading}
        >
          + Thêm sự kiện
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && (
        <p className={`mb-4 ${successMessage.includes("thành công") ? "text-green-500" : "text-red-500"}`}>
          {successMessage}
        </p>
      )}

      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Tìm kiếm theo tên sự kiện..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="min-w-[150px]">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="upcoming">Sắp diễn ra</option>
            <option value="ongoing">Đang diễn ra</option>
            <option value="ended">Đã kết thúc</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Đang tải...</p>
      ) : currentEvents.length === 0 ? (
        <p className="text-center text-gray-500">Không có sự kiện nào để hiển thị.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow-lg">
            <thead>
              <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <th className="p-4 text-left font-semibold">Tên sự kiện</th>
                <th className="p-4 text-left font-semibold">Ngày diễn ra</th>
                <th className="p-4 text-left font-semibold">Địa điểm</th>
                <th className="p-4 text-left font-semibold">Người tham gia</th>
                <th className="p-4 text-left font-semibold">Trạng thái</th>
                <th className="p-4 text-left font-semibold">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {currentEvents.map((event) => (
                <tr key={event._id} className="border-b hover:bg-gray-50 transition duration-200">
                  <td className="p-4 text-gray-800">{event.name}</td>
                  <td className="p-4 text-gray-600">{new Date(event.date).toLocaleDateString("vi-VN")}</td>
                  <td className="p-4 text-gray-600">{event.location}</td>
                  <td className="p-4 text-gray-600">{event.participants?.length || 0}</td>
                  <td className="p-4 text-gray-600">{event.status}</td>
                  <td className="p-4 space-x-2">
                    <button
                      onClick={() => handleViewParticipants(event)}
                      className="px-3 py-1 rounded bg-green-500 text-white hover:bg-green-600 transition duration-200"
                      title="Xem người tham gia"
                    >
                      <FaUsers />
                    </button>
                    <button
                      onClick={() => {
                        setEditingEvent({
                          ...event,
                          date: new Date(event.date).toISOString().split("T")[0],
                        });
                        setShowEditModal(true);
                      }}
                      className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition duration-200"
                      title="Chỉnh sửa sự kiện"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(event._id)}
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition duration-200"
                      title="Xóa sự kiện"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <nav className="inline-flex rounded-md shadow">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              Trước
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`px-3 py-1 border border-gray-300 ${
                  currentPage === page
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-500 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              Sau
            </button>
          </nav>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Thêm sự kiện mới</h3>
            <input
              type="text"
              placeholder="Tên sự kiện"
              value={newEvent.name}
              onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={newEvent.date}
              onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Địa điểm"
              value={newEvent.location}
              onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <textarea
              placeholder="Mô tả"
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200"
              >
                Hủy
              </button>
              <button
                onClick={handleAddEvent}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Chỉnh sửa sự kiện</h3>
            <input
              type="text"
              placeholder="Tên sự kiện"
              value={editingEvent.name}
              onChange={(e) => setEditingEvent({ ...editingEvent, name: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={editingEvent.date}
              onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Địa điểm"
              value={editingEvent.location}
              onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <textarea
              placeholder="Mô tả"
              value={editingEvent.description}
              onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setEditingEvent(null);
                  setShowEditModal(false);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200"
              >
                Hủy
              </button>
              <button
                onClick={handleEditEvent}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200"
              >
                Cập nhật
              </button>
            </div>
          </div>
        </div>
      )}

      {showParticipantsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
            <h3 className="text-xl font-semibold mb-4">Quản lý người tham gia</h3>
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Danh sách người tham gia:</h4>
              {participants.length === 0 ? (
                <p className="text-gray-500">Chưa có người tham gia.</p>
              ) : (
                <ul className="max-h-40 overflow-y-auto">
                  {participants.map((participant) => (
                    <li
                      key={participant._id}
                      className="flex justify-between items-center p-2 border-b"
                    >
                      <span>{participant.name}</span>
                      <button
                        onClick={() => handleLeaveEvent(selectedEventId, participant._id)}
                        className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Xóa
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Thêm người tham gia:</h4>
              <select
                onChange={(e) => {
                  const userId = e.target.value;
                  if (userId) {
                    handleJoinEvent(selectedEventId, userId);
                  }
                }}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Chọn người dùng</option>
                {users
                  .filter((u) => !participants.some((p) => p._id === u._id))
                  .map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowParticipantsModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;